
import React, { useMemo } from 'react';
import { 
  TrendingUp, TrendingDown, Brain, Zap, Sparkles, 
  AlertCircle, Trophy, Target, Clock, Calendar, CheckCircle2
} from 'lucide-react';
import { CalendarData, SubjectProgress, DayData } from '../types';
import { isAfter, subDays, startOfDay, parseISO } from 'date-fns';

interface InsightsProps {
  calendarData: CalendarData;
  subjectProgress: SubjectProgress[];
  language: 'pt' | 'en';
}

const TRANSLATIONS = {
  pt: {
    academicMomentum: 'Impulso Acadêmico',
    momentumText: (pct: number) => `Seu ritmo de estudos cresceu ${pct}% esta semana. Continue assim!`,
    dropAlert: 'Alerta de Queda',
    dropText: (pct: number) => `Houve uma queda de ${pct}% no seu volume de estudos. O que houve?`,
    focusExpert: 'Especialista em Foco',
    focusText: (subject: string) => `Você está focando bastante em ${subject}. Não esqueça de revisar outras áreas.`,
    missingYou: 'Estamos com Saudade',
    missingText: (days: number) => `Faz ${days} dias que não registramos estudos. Vamos retomar hoje com 15 minutos?`,
    startJourney: 'Comece sua Jornada',
    startJourneyText: 'Ainda não detectamos estudos. Que tal planejar sua primeira matéria agora?',
    smallWins: 'Pequenas Vitórias',
    smallWinsText: (count: number) => `Você já finalizou ${count} tópicos do seu plano de longo prazo. Parabéns!`,
    insightsCenter: 'Central de Insights',
    dataIntelligence: 'Inteligência de Dados Parthenon',
    totalTopics: 'Total de Tópicos',
    completedLabel: 'Concluídos',
    inProgressLabel: 'Em Andamento'
  },
  en: {
    academicMomentum: 'Academic Momentum',
    momentumText: (pct: number) => `Your study pace grew ${pct}% this week. Keep it up!`,
    dropAlert: 'Drop Alert',
    dropText: (pct: number) => `There was a ${pct}% drop in your study volume. What happened?`,
    focusExpert: 'Focus Expert',
    focusText: (subject: string) => `You're focusing a lot on ${subject}. Don't forget to review other areas.`,
    missingYou: "We Miss You",
    missingText: (days: number) => `It's been ${days} days since we recorded any studying. Shall we resume today with 15 minutes?`,
    startJourney: 'Start Your Journey',
    startJourneyText: "We haven't detected any studying yet. How about planning your first subject now?",
    smallWins: 'Small Wins',
    smallWinsText: (count: number) => `You've already finished ${count} topics in your long-term plan. Congrats!`,
    insightsCenter: 'Insights Center',
    dataIntelligence: 'Parthenon Data Intelligence',
    totalTopics: 'Total Topics',
    completedLabel: 'Completed',
    inProgressLabel: 'In Progress'
  }
};

export const Insights: React.FC<InsightsProps> = ({ calendarData, subjectProgress, language }) => {
  const t = TRANSLATIONS[language];
  const insights = useMemo(() => {
    const today = startOfDay(new Date());
    const last7DaysLimit = subDays(today, 7);
    const previous7DaysLimit = subDays(today, 14);

    let currentWeekMins = 0;
    let prevWeekMins = 0;
    const subjectCounts: Record<string, number> = {};
    let lastStudyDate: Date | null = null;

    (Object.entries(calendarData) as [string, DayData][]).forEach(([dateStr, day]) => {
      const date = parseISO(dateStr);
      const studyMins = day.studyMinutes || 0;

      if (studyMins > 0) {
        if (!lastStudyDate || date > lastStudyDate) {
          lastStudyDate = date;
        }
      }

      if (isAfter(date, last7DaysLimit)) {
        currentWeekMins += studyMins;
        day.tasks.forEach(t => {
          subjectCounts[t.subject] = (subjectCounts[t.subject] || 0) + 1;
        });
      } else if (isAfter(date, previous7DaysLimit)) {
        prevWeekMins += studyMins;
      }
    });

    const results = [];

    if (prevWeekMins > 0) {
      const diff = ((currentWeekMins - prevWeekMins) / prevWeekMins) * 100;
      if (diff > 5) {
        results.push({
          title: t.academicMomentum,
          icon: TrendingUp,
          text: t.momentumText(Math.round(diff)),
          color: "bg-emerald-500",
          lightColor: "bg-emerald-50 dark:bg-emerald-950/20 border-emerald-400 dark:border-emerald-200"
        });
      } else if (diff < -15) {
        results.push({
          title: t.dropAlert,
          icon: TrendingDown,
          text: t.dropText(Math.abs(Math.round(diff))),
          color: "bg-rose-500",
          lightColor: "bg-rose-50 dark:bg-rose-950/20 border-rose-400 dark:border-rose-200"
        });
      }
    }

    const sortedSubjects = Object.entries(subjectCounts).sort((a, b) => b[1] - a[1]);
    if (sortedSubjects.length > 0) {
      const top = sortedSubjects[0];
      if (top[1] > 3) {
        results.push({
          title: t.focusExpert,
          icon: Brain,
          text: t.focusText(top[0]),
          color: "bg-athena-teal",
          lightColor: "bg-cyan-50 dark:bg-cyan-950/20 border-cyan-400 dark:border-cyan-200"
        });
      }
    }

    if (lastStudyDate) {
        const gap = Math.floor((today.getTime() - lastStudyDate.getTime()) / (1000 * 60 * 60 * 24));
        if (gap >= 2) {
            results.push({
                title: t.missingYou,
                icon: AlertCircle,
                text: t.missingText(gap),
                color: "bg-amber-500",
                lightColor: "bg-amber-50 dark:bg-amber-950/20 border-amber-400 dark:border-amber-200"
            });
        }
    } else {
        results.push({
            title: t.startJourney,
            icon: Sparkles,
            text: t.startJourneyText,
            color: "bg-indigo-500",
            lightColor: "bg-indigo-50 dark:bg-indigo-950/20 border-indigo-400 dark:border-indigo-200"
        });
    }

    const completed = subjectProgress.filter(p => p.status === 'Concluída').length;
    if (completed > 0) {
      results.push({
        title: t.smallWins,
        icon: Trophy,
        text: t.smallWinsText(completed),
        color: "bg-athena-coral",
        lightColor: "bg-orange-50 dark:bg-orange-950/20 border-orange-400 dark:border-orange-200"
      });
    }

    return results;
  }, [calendarData, subjectProgress, t]);

  return (
    <div className="space-y-8 animate-fade-in pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] border-2 border-slate-400 dark:border-slate-800 shadow-xl">
        <div>
          <h2 className="text-3xl md:text-4xl font-black text-slate-950 dark:text-white tracking-tighter flex items-center gap-4">
            <Sparkles className="text-athena-gold" size={36} /> {t.insightsCenter}
          </h2>
          <p className="text-[10px] font-black uppercase text-slate-600 dark:text-slate-500 tracking-[0.2em] mt-2">{t.dataIntelligence}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {insights.map((insight, idx) => (
          <div key={idx} className={`p-8 rounded-[2.5rem] border-2 shadow-lg transition-all hover:scale-[1.02] relative overflow-hidden flex flex-col gap-4 ${insight.lightColor}`}>
            <div className="flex items-center gap-4 relative z-10">
              <div className={`p-3 rounded-2xl text-white shadow-xl ${insight.color}`}>
                <insight.icon size={24} />
              </div>
              <h3 className="text-xl font-black text-slate-950 dark:text-white uppercase tracking-tighter">{insight.title}</h3>
            </div>
            <p className="text-sm font-bold text-slate-800 dark:text-slate-300 leading-relaxed relative z-10">
              {insight.text}
            </p>
            <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                <insight.icon size={120} />
            </div>
          </div>
        ))}
      </div>

      <div className="bg-slate-100 dark:bg-slate-800/40 p-10 rounded-[3rem] border-2 border-slate-400 dark:border-slate-700 shadow-inner grid grid-cols-1 sm:grid-cols-3 gap-8">
        <div className="text-center">
            <div className="mx-auto w-12 h-12 bg-white dark:bg-slate-900 rounded-2xl flex items-center justify-center mb-4 shadow-md text-athena-teal border border-slate-300">
                <Target size={24}/>
            </div>
            <p className="text-[10px] font-black uppercase text-slate-600 dark:text-slate-500 mb-1">{t.totalTopics}</p>
            <p className="text-3xl font-black text-slate-950 dark:text-white">{subjectProgress.length}</p>
        </div>
        <div className="text-center border-y md:border-y-0 md:border-x border-slate-400 dark:border-slate-700 py-6 md:py-0">
            <div className="mx-auto w-12 h-12 bg-white dark:bg-slate-900 rounded-2xl flex items-center justify-center mb-4 shadow-md text-athena-coral border border-slate-300">
                <CheckCircle2 size={24}/>
            </div>
            <p className="text-[10px] font-black uppercase text-slate-600 dark:text-slate-500 mb-1">{t.completedLabel}</p>
            <p className="text-3xl font-black text-slate-950 dark:text-white">{subjectProgress.filter(p => p.status === 'Concluída').length}</p>
        </div>
        <div className="text-center">
            <div className="mx-auto w-12 h-12 bg-white dark:bg-slate-900 rounded-2xl flex items-center justify-center mb-4 shadow-md text-amber-500 border border-slate-300">
                <Clock size={24}/>
            </div>
            <p className="text-[10px] font-black uppercase text-slate-600 dark:text-slate-500 mb-1">{t.inProgressLabel}</p>
            <p className="text-3xl font-black text-slate-950 dark:text-white">{subjectProgress.filter(p => p.status === 'Em andamento').length}</p>
        </div>
      </div>
    </div>
  );
};
